// frontend/src/farm/actionQueue.mjs
// The click-to-act job queue (spec §3): a tap becomes a job; the worker walks to its anchor,
// plays a progress bar, and the scene applies the result on `complete`. Pure state machine;
// the scene supplies `arrived` (from the walker) each tick. No Phaser (node --test).
import { cellKey } from './iso.mjs';

export function createQueue() { return { jobs: [], current: null, progress: 0, blocked: false }; }

const anchorKey = (job) => cellKey(job.anchor.x, job.anchor.y);

export function enqueue(q, job) {
  const key = anchorKey(job);
  const i = q.jobs.findIndex((j) => anchorKey(j) === key);
  if (i >= 0) { q.jobs.splice(i, 1); return false; }
  q.jobs.push(job);
  return true;
}

export function queuedKeys(q) {
  const keys = new Set();
  const all = q.current ? [q.current.job, ...q.jobs] : q.jobs;
  for (const j of all) { if (j.tool) continue; for (const c of j.cells) keys.add(cellKey(c.x, c.y)); }
  return keys;
}

// Vehicle areas for the overlay: the one being worked (green) and those waiting (red).
export function queuedAreas(q) {
  const out = [];
  if (q.current?.job.tool) out.push({ cells: q.current.job.area, working: true });
  for (const j of q.jobs) if (j.tool) out.push({ cells: j.area, working: false });
  return out;
}

export function jobAt(q, cell) {
  return q.jobs.find((j) => (j.area ?? j.cells).some((c) => c.x === cell.x && c.y === cell.y)) ?? null;
}

export function removeJob(q, job) {
  const i = q.jobs.indexOf(job);
  if (i >= 0) q.jobs.splice(i, 1);
  if (i === 0) q.blocked = false;          // the job the pause was about is gone; the next tick re-checks
}

export function clearWaiting(q) { q.jobs = []; q.blocked = false; }

export function currentJob(q) { return q.current?.job ?? null; }

export function clearQueue(q) { q.jobs = []; q.current = null; q.progress = 0; q.blocked = false; }

export function tick(q, dtMs, { arrived = false, canStart = () => true } = {}) {
  const events = [];
  if (!q.current) {
    const next = q.jobs[0];
    if (!next) return events;
    if (!canStart(next)) {                           // e.g. a vehicle waiting for fuel
      if (!q.blocked) { q.blocked = true; events.push({ type: 'blocked', job: next }); }
      return events;
    }
    q.blocked = false;
    q.jobs.shift();
    q.progress = 0;
    if (next.walk === false) {                       // a vehicle pops in: no walk
      q.current = { job: next, phase: 'working' };
      events.push({ type: 'start', job: next });
      return events;
    }
    q.current = { job: next, phase: 'walking' };
    events.push({ type: 'walk', job: next });
    return events;
  }
  const cur = q.current;
  if (cur.phase === 'walking') {
    if (arrived) { cur.phase = 'working'; events.push({ type: 'start', job: cur.job }); }
    return events;
  }
  q.progress = Math.min(1, q.progress + dtMs / cur.job.durationMs);
  events.push({ type: 'progress', job: cur.job, p: q.progress });
  if (q.progress >= 1) {
    events.push({ type: 'complete', job: cur.job });
    q.current = null;
    q.progress = 0;
  }
  return events;
}
